// Frontend ETH/USD price utility with 5-minute localStorage cache and request deduping
// Now using Relay Protocol API instead of CoinGecko to avoid CORS and rate limit issues

const RELAY_API_BASE = 'https://api.relay.link';
const INK_CHAIN_ID = 57073;
const WETH_ADDRESS = '0x4200000000000000000000000000000000000006'; // WETH on Ink

const CACHE_KEY = 'inky_eth_usd_cache_v1';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

interface CachedEthPrice {
  priceUsd: number;
  timestamp: number;
}

let pendingRequest: Promise<number> | null = null;

const getCachedEthPrice = (): number | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedEthPrice = JSON.parse(raw);
    if (Date.now() - cached.timestamp < CACHE_DURATION_MS) {
      return cached.priceUsd;
    }
    return null;
  } catch {
    return null;
  }
};

const setCachedEthPrice = (priceUsd: number) => {
  try {
    const payload: CachedEthPrice = { priceUsd, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
};

const fetchFromRelay = async (): Promise<number> => {
  const url = `${RELAY_API_BASE}/currencies/token/price?address=${WETH_ADDRESS}&chainId=${INK_CHAIN_ID}&referrer=mavrk.ink`;
  
  const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
  if (!res.ok) throw new Error(`Relay API HTTP ${res.status}`);
  
  const json = await res.json();
  const price = json?.price;
  if (typeof price !== 'number' || price <= 0) throw new Error('Relay malformed response or invalid price');
  
  return price;
};

export const getEthUsdPrice = async (): Promise<number> => {
  // Return fresh cache if valid
  const cached = getCachedEthPrice();
  if (cached && Number.isFinite(cached)) return cached;

  // Deduplicate concurrent requests
  if (pendingRequest) return pendingRequest;

  pendingRequest = (async () => {
    try {
      const price = await fetchFromRelay();
      setCachedEthPrice(price);
      return price;
    } catch (e) {
      // Last resort: use stale cache if available regardless of TTL
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) {
          const cachedAny: CachedEthPrice = JSON.parse(raw);
          if (Number.isFinite(cachedAny?.priceUsd)) return cachedAny.priceUsd;
        }
      } catch {
        // ignore
      }
      throw e instanceof Error ? e : new Error('Failed to fetch ETH/USD price from Relay');
    } finally {
      pendingRequest = null;
    }
  })();

  return pendingRequest;
};

